import { useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Menu,
  Tooltip,
  Tab,
  Tabs,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningIcon from '@mui/icons-material/Warning';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorIcon from '@mui/icons-material/Error';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useDebounce } from '@/hooks/useDebounce';
import { DataTable, EntityListResults, type DataTableColumn } from '@/components/common';
import {
  InventoryItemActions,
  InventoryTransactionsDialog,
  PartFormDialog,
  StockAdjustmentDialog,
  StockLocationsDialog,
  LocationManagementDialog,
  BulkImportExportDialog,
  StockTakeDialog,
  type BinLocationFormData,
  type StockTakeResult,
  type InitialStockInput,
} from '@/components/inventory';
import {
  useInventoryPartsPage,
  fetchAllInventoryParts,
  allInventoryPartsQueryKey,
  useInventoryPart,
  useInventoryStats,
  useCreatePart,
  useUpdatePart,
  useStockAdjustment,
  useStockLevels,
  useCreatePurchaseOrder,
  useUpdatePurchaseOrderStatus,
  useCreateBinLocation,
  useUpdateBinLocation,
  useDeleteBinLocation,
  useSuppliers,
  useCategories,
  useWarehouses,
  type InventoryPartsPageFilters,
  type PartListItem,
} from '@/hooks/useInventory';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { neutralGrey } from '@/styles/theme';
import PurchaseOrdersPage from '@/features/purchase-orders/PurchaseOrdersPage';
import PartsDashboardPage from '@/features/inventory/PartsDashboardPage';
import TransitWarehousePage from '@/features/inventory/TransitWarehousePage';
import ReturnsPage from '@/features/inventory/ReturnsPage';
import WarrantyClaimsPage from '@/features/inventory/WarrantyClaimsPage';
import SuppliersTab from '@/features/inventory/SuppliersTab';
import PartBundlesTab from '@/features/inventory/PartBundlesTab';
import StocktakeTab from '@/features/inventory/StocktakeTab';
import {
  PurchaseOrderFormDialog,
  type PurchaseOrderFormData,
} from '@/components/purchase-orders';
import {
  getAllowedInventoryTabs,
  resolveInventoryTab,
  type InventoryTab,
} from './inventoryTabs';
import { useTenantSettings } from '@/hooks/useTenantSettings';

import type { InventoryItem, InventoryTransaction, PartCategory, PurchaseOrderItem, StockStatus } from '@/types';
import { mapMovementToTransaction } from '@/lib/inventoryUtils';
import { useUrlMultiSelectFilter } from '@/hooks/useUrlMultiSelectFilter';

/** Purchase-order surfaces gated by `partsOrderingEnabled` (#2646). Suppliers is one of them (#2272). */
const PARTS_ORDERING_TABS: readonly InventoryTab[] = ['purchaseorders', 'returns', 'warranty', 'suppliers'];

export function applyPartsOrderingVisibility(
  tabs: InventoryTab[],
  partsOrderingEnabled: boolean,
): InventoryTab[] {
  if (partsOrderingEnabled) return tabs;
  return tabs.filter((tab) => !PARTS_ORDERING_TABS.includes(tab));
}

// Map API part to frontend InventoryItem shape
function mapPartToInventoryItem(part: PartListItem): InventoryItem {
  return {
    id: part.id,
    partNumber: part.sku,
    name: part.name,
    description: part.description ?? undefined,
    tags: part.tags ?? [],
    // No fixed-enum category display yet (#724 wires the real category via
    // categoryId/categoryName below) — kept as a harmless legacy filler.
    category: 'other' as PartCategory,
    categoryId: part.categoryId,
    categoryName: part.categoryName ?? undefined,
    brand: undefined,
    oemPartNumber: part.oemPartNumber ?? undefined,
    unit: part.unit,
    isActive: part.isActive,
    unitPriceCents: part.sellPriceCents,
    costPriceCents: part.costPriceCents,
    quantity: part.totalQuantity,
    minStockLevel: part.reorderPoint,
    maxStockLevel: part.maxStockLevel ?? undefined,
    barcode: part.barcode ?? undefined,
    createdAt: part.createdAt,
    updatedAt: part.updatedAt,
  };
}

function getStockStatus(item: InventoryItem): StockStatus {
  if (item.quantity === 0) return 'out_of_stock';
  if (item.quantity <= item.minStockLevel) return 'low_stock';
  return 'in_stock';
}

const PART_SERVER_SORT_KEYS = ['partNumber', 'name', 'quantity', 'unitPrice', 'stockValue', 'usage'] as const;
type PartServerSortKey = (typeof PART_SERVER_SORT_KEYS)[number];

function isPartServerSortKey(key: string): key is PartServerSortKey {
  return (PART_SERVER_SORT_KEYS as readonly string[]).includes(key);
}

// Stock-status colors map to semantic MUI palette tokens (sx string form).
const STATUS_COLORS: Record<string, string> = {
  in_stock: 'success.main',
  low_stock: 'warning.main',
  out_of_stock: 'error.main',
};

const STATUS_ICONS: Record<string, React.ElementType> = {
  in_stock: CheckCircleIcon,
  low_stock: WarningAmberIcon,
  out_of_stock: ErrorIcon,
};

const ALL_FILTERS = ['in_stock', 'low_stock', 'out_of_stock'] as const;

export default function InventoryPage() {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const queryClient = useQueryClient();

  const isMobile = useIsMobile();
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);

  // URL-based multi-select filter state
  const [searchParams, setSearchParams] = useSearchParams();

  // Inventory Management consolidates Stock, Parts needs, Purchase orders,
  // Returns, Warranty claims, Transit warehouse and Suppliers into one page
  // with URL-driven tabs (?tab=…). The active tab is clamped to the tabs the
  // user may actually see, so a crafted ?tab= for a permission the user lacks
  // can never leave the MUI Tabs component on an unmatched value.
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { partsOrderingEnabled } = useTenantSettings();
  const canReadInventory = hasPermission('inventory.read');
  const canViewPurchaseOrders = hasPermission('purchase_orders.read');
  const canCreatePart = hasPermission('inventory.create');
  const canUpdatePart = hasPermission('inventory.update');
  const canAdjustStock = hasPermission('inventory.adjust');
  const canCreatePurchaseOrder = hasPermission('purchase_orders.create');
  const showPurchaseOrderTabs = canViewPurchaseOrders && partsOrderingEnabled;
  const allowedTabs = applyPartsOrderingVisibility(
    getAllowedInventoryTabs({ canReadInventory, canViewPurchaseOrders }),
    partsOrderingEnabled,
  );
  const activeTab = resolveInventoryTab(searchParams.get('tab'), allowedTabs);
  const handleTabChange = useCallback((_: React.SyntheticEvent, value: InventoryTab) => {
    const next = new URLSearchParams(searchParams);
    // 'stock' is the default tab and carries no ?tab param so its URL stays clean.
    if (value === 'stock') {
      next.delete('tab');
    } else {
      next.set('tab', value);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /*
    #878 extracted this into useUrlMultiSelectFilter — but nothing ever
    called the hook, so all four pages kept their own verbatim copy of
    ~50 lines (#2726). searchParams stays: this page also drives its tabs
    from the URL.
  */
  const { selectedFilters, setSelectedFilters } = useUrlMultiSelectFilter({
    allFilters: ALL_FILTERS,
    storageKey: 'vs-filter-inventory',
  });

  const handleToggleFilter = useCallback((filterKey: string) => {
    const isSelected = selectedFilters.includes(filterKey);
    if (isSelected) {
      setSelectedFilters(selectedFilters.filter((s) => s !== filterKey));
    } else {
      setSelectedFilters([...selectedFilters, filterKey]);
    }
  }, [selectedFilters, setSelectedFilters]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 300);

  // Category filter
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Dashboard stats (always active)
  const { data: stats } = useInventoryStats();

  // Status chips that are not "all" are filtered on the server so the total
  // matches the page. None selected stays an empty table without changing the
  // query, or keepPreviousData would show the previous page.
  const allSelected =
    selectedFilters.length === ALL_FILTERS.length &&
    ALL_FILTERS.every((status) => selectedFilters.includes(status));

  const partFilters = useMemo(() => {
    const next: InventoryPartsPageFilters = {};
    if (debouncedQuery) next.q = debouncedQuery;
    if (categoryFilter !== 'all') next.categoryId = categoryFilter;
    if (!allSelected && selectedFilters.length > 0) {
      next.stockStatus = [...selectedFilters].sort().join(',');
    }
    return next;
  }, [debouncedQuery, categoryFilter, allSelected, selectedFilters]);

  const {
    rows: partRows,
    total,
    isLoading,
    isError,
    error,
    refetch,
    sort,
    setSort,
    pagination,
  } = useInventoryPartsPage(partFilters);

  const suppliersQuery = useSuppliers();
  const categoriesQuery = useCategories();
  const warehousesQuery = useWarehouses();
  const createPartMutation = useCreatePart();
  const updatePartMutation = useUpdatePart();
  const stockAdjustmentMutation = useStockAdjustment();
  const createPurchaseOrderMutation = useCreatePurchaseOrder();
  const updatePurchaseOrderStatusMutation = useUpdatePurchaseOrderStatus();
  const createBinLocationMutation = useCreateBinLocation();
  const updateBinLocationMutation = useUpdateBinLocation();
  const deleteBinLocationMutation = useDeleteBinLocation();

  const pageItems = useMemo(() => partRows.map(mapPartToInventoryItem), [partRows]);
  const visibleItems = useMemo(
    () => (selectedFilters.length === 0 ? [] : pageItems),
    [pageItems, selectedFilters],
  );

  // Status count map from stats
  const statusCountMap = useMemo(() => ({
    in_stock: (stats?.totalCount ?? 0) - (stats?.lowStockCount ?? 0) - (stats?.outOfStockCount ?? 0),
    low_stock: stats?.lowStockCount ?? 0,
    out_of_stock: stats?.outOfStockCount ?? 0,
  }), [stats]);

  // Map categories from API for the form
  const categoryList = categoriesQuery.data?.data ?? [];

  // Map suppliers from API for the form
  const suppliersList = useMemo(() => {
    const pages = suppliersQuery.data?.pages ?? [];
    return pages.flatMap((page) =>
      page.data.map((s) => ({
        id: s.id,
        name: s.name,
        deliveryDays: s.defaultLeadTimeDays,
      })),
    );
  }, [suppliersQuery.data]);

  // Warehouses for the initial-stock section of the part form and for
  // stock adjustments; prefer the tenant's configured default warehouse.
  const warehouseList = useMemo(() => warehousesQuery.data?.data ?? [], [warehousesQuery.data]);
  const defaultWarehouseId = warehouseList.find((w) => w.isDefault)?.id ?? warehouseList[0]?.id;

  // UI state
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [stockLocationsOpen, setStockLocationsOpen] = useState(false);
  const [transactionsOpen, setTransactionsOpen] = useState(false);
  const [partFormOpen, setPartFormOpen] = useState(false);
  const [editingPart, setEditingPart] = useState<InventoryItem | null>(null);

  // The list view never carries detail-only fields (e.g. reorderQuantity), so
  // editing would otherwise show a wrong default and silently overwrite the
  // real stored value on save. Fetch the full part before letting the form
  // open (#724).
  const editingPartDetailQuery = useInventoryPart(editingPart?.id);
  const editingPartForDialog = useMemo<InventoryItem | null>(() => {
    if (!editingPart) return null;
    const detail = editingPartDetailQuery.data;
    if (!detail || detail.id !== editingPart.id) return null;
    return { ...editingPart, reorderQuantity: detail.reorderQuantity, tags: detail.tags ?? [] };
  }, [editingPart, editingPartDetailQuery.data]);
  const isPartFormOpen = partFormOpen && (!editingPart || !!editingPartForDialog);

  // Fetch detail when balances or transaction history are opened.
  const selectedItemDetailQuery = useInventoryPart(transactionsOpen || stockLocationsOpen ? selectedItem?.id : undefined);
  const selectedItemTransactions = useMemo<InventoryTransaction[]>(() => {
    const detail = selectedItemDetailQuery.data;
    if (!detail || !selectedItem || detail.id !== selectedItem.id) return [];
    return detail.recentMovements.map((m) => mapMovementToTransaction(detail.id, m));
  }, [selectedItemDetailQuery.data, selectedItem]);

  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [adjustingItem, setAdjustingItem] = useState<InventoryItem | null>(null);
  const adjustingDetailQuery = useInventoryPart(adjustmentDialogOpen ? adjustingItem?.id : undefined);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [importExportDialogOpen, setImportExportDialogOpen] = useState(false);
  const [stockTakeDialogOpen, setStockTakeDialogOpen] = useState(false);
  // Actions that work on the whole matching set, not the page on screen (#2741).
  const [fullListItems, setFullListItems] = useState<InventoryItem[]>([]);
  const stockTakeLevelsQuery = useStockLevels(stockTakeDialogOpen);
  // One stock-take row per stock location (#2755), over every part matching
  // the filters — not just the page on screen (#2741).
  const stockTakeItems = useMemo(() => fullListItems.flatMap((item) => {
    const levels = stockTakeLevelsQuery.data?.data.filter((l) => l.partId === item.id) ?? [];
    if (levels.length === 0 && defaultWarehouseId) {
      return [{
        ...item,
        id: `unlocated-${item.id}`,
        partId: item.id,
        warehouseId: defaultWarehouseId,
        binLocationId: null,
        quantity: 0,
        location: `${warehouseList.find((w) => w.id === defaultWarehouseId)?.name ?? ''} / ${t('stockLocations.noBin')}`,
      }];
    }
    return levels.map((level) => ({
      ...item,
      id: level.id,
      partId: item.id,
      warehouseId: level.warehouseId,
      binLocationId: level.binLocationId,
      quantity: level.quantityOnHand,
      location: `${level.warehouseName} / ${level.binLocationCode ?? t('stockLocations.noBin')}`,
    }));
  }), [fullListItems, stockTakeLevelsQuery.data, t, defaultWarehouseId, warehouseList]);
  const [poFormOpen, setPoFormOpen] = useState(false);
  const [poInventory, setPoInventory] = useState<InventoryItem[]>([]);
  const [loadingFullList, setLoadingFullList] = useState(false);
  const [poInitialItems, setPoInitialItems] = useState<PurchaseOrderItem[]>([]);

  // Handlers
  const handleViewStockLocations = (item: InventoryItem) => {
    setSelectedItem(item);
    setStockLocationsOpen(true);
  };
  const handleViewTransactions = (item: InventoryItem) => {
    setSelectedItem(item);
    setTransactionsOpen(true);
  };

  const handleCloseTransactions = () => {
    setTransactionsOpen(false);
    setSelectedItem(null);
  };

  // Build a purchase-order line from an inventory item, defaulting the ordered
  // quantity toward the reorder point and pricing it at the part's cost. #1166
  const inventoryItemToPoLine = (item: InventoryItem): PurchaseOrderItem => {
    const shortfall = Math.max(item.minStockLevel - item.quantity, 0);
    const quantity = shortfall > 0 ? shortfall : 1;
    return {
      id: `initial-${item.id}`,
      inventoryItemId: item.id,
      partNumber: item.partNumber,
      name: item.name,
      description: item.description,
      quantity,
      unitCostCents: item.costPriceCents,
      totalCostCents: Math.round(quantity * item.costPriceCents),
    };
  };

  // Order a single part: open the real purchase-order form with the part
  // pre-added, so the user only picks a supplier and confirms. #1166
  /** Reads every part matching `filters` before a whole-list action opens. */
  const withAllParts = async (
    filters: InventoryPartsPageFilters,
    open: (items: InventoryItem[]) => void,
  ) => {
    setLoadingFullList(true);
    try {
      const allParts = await queryClient.fetchQuery({
        queryKey: allInventoryPartsQueryKey(filters),
        queryFn: () => fetchAllInventoryParts(filters),
        staleTime: 30_000,
      });
      open(allParts.map(mapPartToInventoryItem));
    } catch {
      notify.error(tc('error'), t('errorLoadingAll'));
    } finally {
      setLoadingFullList(false);
    }
  };

  const handleOrderStock = (item: InventoryItem) => {
    void withAllParts({}, (allItems) => {
      setPoInventory(allItems);
      setPoInitialItems([inventoryItemToPoLine(item)]);
      setPoFormOpen(true);
    });
  };

  // "Luo tilaus (N)" toolbar action: seed the form with every low/out-of-stock
  // part, so the whole restock list is one draft PO (#1166). N is the
  // tenant-wide count, so this reads every part, not the page on screen (#2741).
  const handleCreateOrderFromLowStock = () => {
    void withAllParts({}, (allItems) => {
      setPoInventory(allItems);
      setPoInitialItems(
        allItems.filter((i) => getStockStatus(i) !== 'in_stock').map(inventoryItemToPoLine),
      );
      setPoFormOpen(true);
    });
  };

  // Stock-take and import/export act on the list as filtered, across every page.
  const openWithFilteredList = (open: () => void) => {
    if (selectedFilters.length === 0) {
      setFullListItems([]);
      open();
      return;
    }
    void withAllParts(partFilters, (items) => {
      setFullListItems(items);
      open();
    });
  };
  const openStockTake = () => openWithFilteredList(() => setStockTakeDialogOpen(true));
  const openImportExport = () => openWithFilteredList(() => setImportExportDialogOpen(true));

  const handleClosePoForm = () => {
    setPoFormOpen(false);
    setPoInitialItems([]);
  };

  const handlePoSave = (data: PurchaseOrderFormData, submitForApproval: boolean) => {
    const lines = data.items.map((item) => ({
      partId: item.inventoryItemId ?? '',
      quantityOrdered: item.quantity,
      unitPriceCents: item.unitCostCents,
      ...(item.linkedBookingId ? { linkedBookingId: item.linkedBookingId } : {}),
    }));

    createPurchaseOrderMutation.mutate(
      {
        supplierId: data.supplierId,
        warehouseId: data.warehouseId,
        notes: data.notes,
        shippingCents: data.shippingCents,
        lines,
      },
      {
        onSuccess: (result) => {
          notify.success(
            t('purchaseOrders.orderCreated'),
            submitForApproval ? t('purchaseOrders.sentForApproval') : t('purchaseOrders.asDraft'),
          );
          // "Submit for approval" creates the draft, then transitions it to
          // approved — the backend has no separate "pending" state.
          if (submitForApproval) {
            updatePurchaseOrderStatusMutation.mutate({ id: result.id, status: 'approved' });
          }
          handleClosePoForm();
        },
        onError: () => notify.error(tc('error'), t('purchaseOrders.createFailed')),
      },
    );
  };

  const handleAddItem = () => {
    setEditingPart(null);
    setPartFormOpen(true);
  };

  const handleEditItem = (item: InventoryItem) => {
    setEditingPart(item);
    setPartFormOpen(true);
  };

  const handleSavePart = (partData: Partial<InventoryItem>, initialStock: InitialStockInput | null) => {
    if (editingPart) {
      updatePartMutation.mutate(
        {
          id: editingPart.id,
          data: {
            name: partData.name,
            sku: partData.partNumber,
            description: partData.description ?? null,
            tags: partData.tags,
            categoryId: partData.categoryId,
            oemPartNumber: partData.oemPartNumber ?? null,
            barcode: partData.barcode ?? null,
            unit: partData.unit,
            isActive: partData.isActive,
            costPriceCents: partData.costPriceCents,
            sellPriceCents: partData.unitPriceCents,
            reorderPoint: partData.minStockLevel,
            reorderQuantity: partData.reorderQuantity,
            maxStockLevel: partData.maxStockLevel,
          },
        },
        {
          onSuccess: () => notify.success(t('notifications.partUpdated'), `${partData.name}`),
          onError: () => notify.error(tc('error'), t('notifications.partUpdateFailed')),
        },
      );
      return;
    }

    if (!partData.categoryId) {
      notify.error(tc('error'), t('notifications.noCategoriesAvailable'));
      return;
    }

    createPartMutation.mutate(
      {
        sku: partData.partNumber ?? `SKU-${Date.now()}`,
        name: partData.name ?? t('notifications.newPart'),
        categoryId: partData.categoryId,
        tags: partData.tags,
        unit: partData.unit,
        isActive: partData.isActive,
        costPriceCents: partData.costPriceCents ?? 0,
        sellPriceCents: partData.unitPriceCents ?? 0,
        reorderPoint: partData.minStockLevel ?? 5,
        reorderQuantity: partData.reorderQuantity,
        maxStockLevel: partData.maxStockLevel,
        ...(partData.description ? { description: partData.description } : {}),
        ...(partData.oemPartNumber ? { oemPartNumber: partData.oemPartNumber } : {}),
        ...(partData.barcode ? { barcode: partData.barcode } : {}),
      },
      {
        onSuccess: (created) => {
          notify.success(t('notifications.partAdded'), `${partData.name}`);
          if (initialStock && initialStock.quantity > 0) {
            stockAdjustmentMutation.mutate(
              {
                partId: created.id,
                warehouseId: initialStock.warehouseId,
                quantity: initialStock.quantity,
                movementType: 'adjustment',
                notes: initialStock.note.trim() || t('notifications.initialStockNote'),
                ...(initialStock.expiryDate ? { expiryDate: initialStock.expiryDate } : {}),
              },
              {
                onSuccess: () => notify.success(t('notifications.stockUpdated'), t('notifications.initialStockNote')),
                onError: () => notify.error(tc('error'), t('notifications.stockAdjustFailed')),
              },
            );
          }
        },
        onError: () => notify.error(tc('error'), t('notifications.partAddFailed')),
      },
    );
  };

  const handleClosePartForm = () => {
    setPartFormOpen(false);
    setEditingPart(null);
  };

  const handleAdjustStock = (item: InventoryItem) => {
    setAdjustingItem(item);
    setAdjustmentDialogOpen(true);
  };

  const handleStockAdjustment = async (adjustment: {
    warehouseId: string;
    binLocationId: string | null;
    type: string;
    quantity: number;
    reason: string;
    notes?: string;
    expiryDate?: string | null;
  }) => {
    if (!adjustingItem) return;

    await stockAdjustmentMutation.mutateAsync(
      {
        partId: adjustingItem.id,
        warehouseId: adjustment.warehouseId,
        binLocationId: adjustment.binLocationId,
        quantity: adjustment.quantity,
        movementType: adjustment.type || 'adjustment',
        notes: adjustment.notes ?? adjustment.reason,
        ...(adjustment.expiryDate !== undefined ? { expiryDate: adjustment.expiryDate } : {}),
      },
      {
        onSuccess: () => {
          const amount = Math.abs(adjustment.quantity);
          notify.success(t('notifications.stockUpdated'), t('notifications.stockAdjusted', { amount, reason: adjustment.reason }));
        },
        onError: () => notify.error(tc('error'), t('notifications.stockAdjustFailed')),
      },
    );
  };

  const handleCloseAdjustmentDialog = () => {
    setAdjustmentDialogOpen(false);
    setAdjustingItem(null);
  };

  // Empty coordinate strings collapse to null so the backend stores an absent
  // coordinate rather than an empty string. #1166
  const binPayload = (data: BinLocationFormData) => ({
    code: data.code.trim(),
    description: data.description.trim() || null,
    zone: data.zone.trim() || null,
    aisle: data.aisle.trim() || null,
    rack: data.rack.trim() || null,
    shelf: data.shelf.trim() || null,
  });

  const handleCreateBinLocation = (warehouseId: string, data: BinLocationFormData) => {
    createBinLocationMutation.mutate(
      { warehouseId, ...binPayload(data) },
      {
        onSuccess: () => notify.success(t('notifications.locationAdded'), data.code),
        onError: () => notify.error(tc('error'), t('notifications.locationSaveFailed')),
      },
    );
  };

  const handleUpdateBinLocation = (id: string, data: BinLocationFormData) => {
    updateBinLocationMutation.mutate(
      { id, data: binPayload(data) },
      {
        onSuccess: () => notify.success(t('notifications.locationUpdated'), data.code),
        onError: () => notify.error(tc('error'), t('notifications.locationSaveFailed')),
      },
    );
  };

  const handleDeleteBinLocation = (id: string) => {
    deleteBinLocationMutation.mutate(id, {
      onSuccess: () => notify.success(t('notifications.locationDeleted'), t('notifications.locationDeletedDesc')),
      onError: () => notify.error(tc('error'), t('notifications.locationSaveFailed')),
    });
  };

  const handleBulkImport = (items: Record<string, unknown>[]) => {
    notify.success(t('notifications.importSuccess'), t('notifications.importSuccessDesc', { count: items.length }));
  };

  const handleStockTakeComplete = async (results: StockTakeResult[]) => {
    // Only rows whose counted quantity differs from the system need a
    // correction; matches require no movement. A 'count' adjustment carries
    // the absolute counted quantity — the backend derives the delta.
    const corrections = results.filter((r) => r.discrepancy !== 0);
    if (corrections.length === 0) {
      notify.success(
        t('notifications.stockTakeSaved'),
        t('notifications.stockTakeNoDiscrepancies', { count: results.length }),
      );
      return;
    }

    const outcomes = await Promise.allSettled(
      corrections.map((r) =>
        stockAdjustmentMutation.mutateAsync({
          partId: r.itemId,
          warehouseId: r.warehouseId,
          binLocationId: r.binLocationId,
          quantity: r.countedQuantity,
          movementType: 'count',
          notes: r.notes || t('notifications.stockTakeReason'),
        }),
      ),
    );

    const failed = outcomes.filter((o) => o.status === 'rejected').length;
    const applied = corrections.length - failed;

    if (failed === 0) {
      notify.success(
        t('notifications.stockTakeSaved'),
        t('notifications.stockTakeSavedDesc', { count: results.length, discrepancies: applied }),
      );
    } else {
      notify.error(tc('error'), t('notifications.stockTakePartialFail', { applied, failed }));
    }
  };

  const handleSortChange = useCallback((key: string, direction: 'asc' | 'desc') => {
    if (!isPartServerSortKey(key)) return;
    setSort({ sortBy: key, sortDir: direction });
  }, [setSort]);

  const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2)}`;

  const getStockStatusColor = (item: InventoryItem) => {
    const status = getStockStatus(item);
    switch (status) {
      case 'out_of_stock':
        return 'error';
      case 'low_stock':
        return 'warning';
      default:
        return 'success';
    }
  };

  const getStockStatusIcon = (item: InventoryItem) => {
    const status = getStockStatus(item);
    switch (status) {
      case 'out_of_stock':
        return <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />;
      case 'low_stock':
        return <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  const renderItemActions = (item: InventoryItem) => (
    <InventoryItemActions
      item={item}
      onEdit={canUpdatePart ? handleEditItem : undefined}
      onAdjustStock={canAdjustStock ? handleAdjustStock : undefined}
      onLocations={handleViewStockLocations}
      onTransactions={handleViewTransactions}
      onOrder={canCreatePurchaseOrder && getStockStatus(item) !== 'in_stock' ? handleOrderStock : undefined}
    />
  );

  const columns: DataTableColumn<InventoryItem>[] = [
    {
      header: t('partNumber'),
      sortKey: 'partNumber',
      renderCell: (item) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InventoryIcon fontSize="small" color="action" />
          </Box>
          <Box>
            <Chip
              label={item.partNumber}
              size="small"
              variant="outlined"
              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
            />
            {item.location && (
              <Typography variant="caption" color="text.secondary" display="block">
                {item.location}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      header: t('name'),
      sortKey: 'name',
      renderCell: (item) => (
        <>
          <Typography variant="body2" fontWeight={500}>
            {item.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {item.brand}
          </Typography>
        </>
      ),
    },
    {
      header: t('category'),
      renderCell: (item) => (
        <Chip
          label={item.categoryName ?? t('uncategorized')}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      header: t('supplier'),
      renderCell: (item) => (
        <Typography variant="body2">{item.supplier?.name || '-'}</Typography>
      ),
    },
    {
      header: t('stock'),
      sortKey: 'quantity',
      renderCell: (item) => {
        const stockPercentage = item.maxStockLevel
          ? Math.min(100, (item.quantity / item.maxStockLevel) * 100)
          : 50;
        return (
          <Box sx={{ minWidth: 100 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              {getStockStatusIcon(item)}
              <Typography
                variant="body2"
                fontWeight={500}
                color={`${getStockStatusColor(item)}.main`}
              >
                {item.quantity}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                / {item.minStockLevel}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={stockPercentage}
              color={getStockStatusColor(item)}
              sx={{ height: 4, borderRadius: 1 }}
            />
          </Box>
        );
      },
    },
    {
      header: t('price'),
      sortKey: 'unitPrice',
      renderCell: (item) => (
        <>
          <Typography variant="body2" fontWeight={500}>
            {formatPrice(item.unitPriceCents)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('cost')}: {formatPrice(item.costPriceCents)}
          </Typography>
        </>
      ),
    },
    {
      header: t('value'),
      sortKey: 'stockValue',
      renderCell: (item) => (
        <Typography variant="body2" fontWeight={500}>
          {formatPrice(item.quantity * item.costPriceCents)}
        </Typography>
      ),
    },
    {
      header: t('actions'),
      align: 'center',
      renderCell: (item) => renderItemActions(item),
    },
  ];

  return (
    <Box>
      {/* Tabs: Stock · Parts needs · Purchase orders · Returns · Warranty claims · Transit warehouse · Part bundles · Stocktake · Suppliers */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {canReadInventory && <Tab value="stock" label={t('tabs.stock')} />}
        {canReadInventory && <Tab value="needs" label={t('tabs.partsNeeds')} />}
        {showPurchaseOrderTabs && <Tab value="purchaseorders" label={t('tabs.purchaseOrders')} />}
        {showPurchaseOrderTabs && <Tab value="returns" label={t('tabs.returns')} />}
        {showPurchaseOrderTabs && <Tab value="warranty" label={t('tabs.warranty')} />}
        {canReadInventory && <Tab value="transit" label={t('tabs.transit')} />}
        {allowedTabs.includes('bundles') && <Tab value="bundles" label={t('tabs.bundles')} />}
        {canReadInventory && <Tab value="stocktake" label={t('tabs.stocktake')} />}
        {allowedTabs.includes('suppliers') && <Tab value="suppliers" label={t('tabs.suppliers')} />}
      </Tabs>

      {activeTab === 'purchaseorders' && showPurchaseOrderTabs && <PurchaseOrdersPage />}
      {activeTab === 'returns' && showPurchaseOrderTabs && <ReturnsPage />}
      {activeTab === 'warranty' && showPurchaseOrderTabs && <WarrantyClaimsPage />}
      {activeTab === 'needs' && canReadInventory && <PartsDashboardPage />}
      {activeTab === 'transit' && canReadInventory && <TransitWarehousePage />}
      {activeTab === 'bundles' && allowedTabs.includes('bundles') && <PartBundlesTab />}
      {activeTab === 'stocktake' && canReadInventory && <StocktakeTab />}
      {activeTab === 'suppliers' && allowedTabs.includes('suppliers') && <SuppliersTab />}
      {activeTab === 'stock' && canReadInventory && (
      <>
      {/* Header: title + add button */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={600}>
          {t('title')}
        </Typography>
        {canCreatePart && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddItem}>
            {t('addItem')}
          </Button>
        )}
      </Box>

      {/* Search bar */}
      <TextField
        size="small"
        placeholder={t('searchPlaceholder')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ width: { xs: '100%', md: 500 }, mb: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <Tooltip title={t('clearSearch')}>
                  <IconButton
                    size="small"
                    onClick={() => setSearchQuery('')}
                    aria-label={t('clearSearch')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          },
        }}
      />

      {/* Multi-select stock status chip toggles */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        {ALL_FILTERS.map((filterKey) => {
          const isSelected = selectedFilters.includes(filterKey);
          const count = statusCountMap[filterKey] ?? 0;
          const color = STATUS_COLORS[filterKey] ?? neutralGrey;
          const IconComp = STATUS_ICONS[filterKey] ?? CheckCircleIcon;

          return (
            <Tooltip key={filterKey} title={t(`stockStatus.${filterKey}`)}>
              <Chip
                icon={<IconComp sx={{ fontSize: 16 }} />}
                label={count}
                size="small"
                onClick={() => handleToggleFilter(filterKey)}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  minWidth: 52,
                  ...(isSelected
                    ? {
                        bgcolor: color,
                        color: 'common.white',
                        '& .MuiChip-icon': { color: 'common.white' },
                        '&:hover': { bgcolor: color, opacity: 0.85 },
                      }
                    : {
                        bgcolor: 'transparent',
                        border: '1px solid',
                        borderColor: 'divider',
                        color: 'text.secondary',
                        '& .MuiChip-icon': { color },
                        '&:hover': { bgcolor: 'action.hover' },
                      }),
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('category')}</InputLabel>
          <Select
            value={categoryFilter}
            label={t('category')}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <MenuItem value="all">{t('allCategories')}</MenuItem>
            {categoryList.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flex: 1 }} />

        {isMobile ? (
          <>
            <Tooltip title={tc('moreOptions')}>
              <IconButton onClick={(e) => setMoreMenuAnchor(e.currentTarget)} aria-label={tc('moreOptions')}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={moreMenuAnchor}
              open={Boolean(moreMenuAnchor)}
              onClose={() => setMoreMenuAnchor(null)}
            >
              {canCreatePurchaseOrder && (
                <MenuItem onClick={() => { setMoreMenuAnchor(null); handleCreateOrderFromLowStock(); }}>
                  <ShoppingCartIcon sx={{ mr: 1 }} fontSize="small" />
                  {t('createOrder')} ({stats?.lowStockCount ?? 0})
                </MenuItem>
              )}
              <MenuItem onClick={() => { setMoreMenuAnchor(null); openStockTake(); }}>
                <AssessmentIcon sx={{ mr: 1 }} fontSize="small" />
                {t('purchaseOrderForm.inventoryAction')}
              </MenuItem>
              <MenuItem onClick={() => { setMoreMenuAnchor(null); openImportExport(); }}>
                <ImportExportIcon sx={{ mr: 1 }} fontSize="small" />
                {t('purchaseOrderForm.importExport')}
              </MenuItem>
              <MenuItem onClick={() => { setMoreMenuAnchor(null); setLocationDialogOpen(true); }}>
                <WarehouseIcon sx={{ mr: 1 }} fontSize="small" />
                {t('purchaseOrderForm.warehouseLocations')}
              </MenuItem>
            </Menu>
          </>
        ) : (
          <>
            {canCreatePurchaseOrder && (
              <Button
                variant="outlined"
                startIcon={<ShoppingCartIcon />}
                color="warning"
                onClick={handleCreateOrderFromLowStock}
                disabled={loadingFullList}
              >
                {t('createOrder')} ({stats?.lowStockCount ?? 0})
              </Button>
            )}

            <Button
              variant="outlined"
              startIcon={<AssessmentIcon />}
              onClick={openStockTake}
              disabled={loadingFullList}
            >
              {t('purchaseOrderForm.inventoryAction')}
            </Button>

            <Button
              variant="outlined"
              startIcon={<ImportExportIcon />}
              onClick={openImportExport}
              disabled={loadingFullList}
            >
              {t('purchaseOrderForm.importExport')}
            </Button>

            <Button
              variant="outlined"
              startIcon={<WarehouseIcon />}
              onClick={() => setLocationDialogOpen(true)}
            >
              {t('purchaseOrderForm.warehouseLocations')}
            </Button>
          </>
        )}
      </Box>

      {/* Results */}
      <EntityListResults
        loading={isLoading}
        isError={isError}
        errorText={t('errorLoading')}
        error={error}
        onRefetch={() => {
          void refetch();
        }}
        resultCountText={t('searchResultCount', {
          count: selectedFilters.length === 0 ? 0 : total,
        })}
        showSelectStatusWarning={selectedFilters.length === 0}
        pagination={
          selectedFilters.length === 0 ? { ...pagination, total: 0, page: 0 } : pagination
        }
      >
          <DataTable<InventoryItem>
            columns={columns}
            data={visibleItems}
            getRowKey={(item) => item.id}
            sortKey={sort?.sortBy ?? 'quantity'}
            sortDirection={sort?.sortDir ?? 'asc'}
            onSortChange={handleSortChange}
            emptyMessage={t('noItems')}
            emptyDescription={t('noItemsDesc')}
            loading={isLoading}
            renderMobileCard={(item) => (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Chip
                    label={item.partNumber}
                    size="small"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {formatPrice(item.unitPriceCents)}
                    </Typography>
                    {renderItemActions(item)}
                  </Box>
                </Box>
                <Typography variant="body2" fontWeight={500}>
                  {item.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  {getStockStatusIcon(item)}
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color={`${getStockStatusColor(item)}.main`}
                  >
                    {item.quantity}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    / {item.minStockLevel}
                  </Typography>
                </Box>
              </Box>
            )}
          />
      </EntityListResults>

      <StockLocationsDialog
        item={selectedItem}
        levels={selectedItemDetailQuery.data?.stockLevels ?? []}
        open={stockLocationsOpen}
        onClose={() => { setStockLocationsOpen(false); setSelectedItem(null); }}
        loading={selectedItemDetailQuery.isLoading}
        error={selectedItemDetailQuery.isError}
        onRetry={() => void selectedItemDetailQuery.refetch()}
      />

      {/* Transaction history */}
      <InventoryTransactionsDialog
        item={selectedItem}
        transactions={selectedItemTransactions}
        open={transactionsOpen}
        onClose={handleCloseTransactions}
        loading={selectedItemDetailQuery.isLoading}
        error={selectedItemDetailQuery.isError}
        onRetry={() => void selectedItemDetailQuery.refetch()}
      />

      {/* Part Add/Edit Dialog */}
      <PartFormDialog
        open={isPartFormOpen}
        onClose={handleClosePartForm}
        onSave={handleSavePart}
        part={editingPartForDialog}
        suppliers={suppliersList}
        categories={categoryList}
        warehouses={warehouseList}
      />

      {/* Stock Adjustment Dialog */}
      <StockAdjustmentDialog
        open={adjustmentDialogOpen}
        onClose={handleCloseAdjustmentDialog}
        item={adjustingItem}
        onAdjust={handleStockAdjustment}
        warehouses={warehouseList}
        levels={adjustingDetailQuery.data?.stockLevels ?? []}
        loading={adjustingDetailQuery.isLoading || warehousesQuery.isLoading}
        error={adjustingDetailQuery.isError || warehousesQuery.isError}
        saving={stockAdjustmentMutation.isPending}
        onRetry={() => { void adjustingDetailQuery.refetch(); void warehousesQuery.refetch(); }}
      />

      {/* Location Management Dialog */}
      <LocationManagementDialog
        open={locationDialogOpen}
        onClose={() => setLocationDialogOpen(false)}
        warehouses={warehouseList}
        onCreate={handleCreateBinLocation}
        onUpdate={handleUpdateBinLocation}
        onDelete={handleDeleteBinLocation}
      />

      {/* Bulk Import/Export Dialog */}
      <BulkImportExportDialog
        open={importExportDialogOpen}
        onClose={() => setImportExportDialogOpen(false)}
        items={fullListItems}
        onImport={handleBulkImport}
      />

      {stockTakeDialogOpen && stockTakeLevelsQuery.isLoading && <LinearProgress aria-label={tc('loading')} />}
      {stockTakeDialogOpen && stockTakeLevelsQuery.isError && <Alert severity="error" action={<Button onClick={() => void stockTakeLevelsQuery.refetch()}>{tc('errors.refresh')}</Button>}>{t('errorLoading')}</Alert>}
      {/* Stock Take Dialog */}
      <StockTakeDialog
        open={stockTakeDialogOpen && !stockTakeLevelsQuery.isLoading && !stockTakeLevelsQuery.isError}
        onClose={() => setStockTakeDialogOpen(false)}
        items={stockTakeItems}
        onComplete={handleStockTakeComplete}
      />

      {/* Purchase Order Create Dialog (from low-stock action / order-this-part) */}
      <PurchaseOrderFormDialog
        open={poFormOpen}
        onClose={handleClosePoForm}
        suppliers={suppliersList}
        inventory={poInventory}
        warehouses={warehouseList}
        initialItems={poInitialItems}
        onSave={handlePoSave}
      />
      </>
      )}
    </Box>
  );
}
